import { useFormikContext } from "formik";
import { AgeSurchargeType, Currency, UpdateSupplierInput } from "@/gql/graphql";
import { AgeSurchargeTypeInput } from "@/app/Common/Components/AgeSurchargeTypeInput";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { AgeSurchargeIndicator } from "@/app/Common/Components/AgeSurchargeIndicator";

export function SupplierAgeRequirementForm({
  currency,
}: {
  currency: Currency;
}) {
  const { values } = useFormikContext<UpdateSupplierInput>();

  return (
    <>
      <NumberInput name="minimum_age" label="Minimum age" />
      <NumberInput name="maximum_age" label="Maximum age" />
      <SwitchInput name="has_age_surcharge" label="Age surcharge" />
      <div className="col-span-3" />

      {values.has_age_surcharge ? (
        <>
          <NumberInput
            name="minimum_no_surcharge_age"
            label="Minimum age (no surcharge)"
          />

          <NumberInput
            name="maximum_no_surcharge_age"
            label="Maximum age (no surcharge)"
          />
          <AgeSurchargeTypeInput
            name="age_surcharge_type"
            label="Surcharge type"
          />

          <SurchargeAmountInput currency={currency} />
          <SupplierAgeSurchargePreview currency={currency} />
        </>
      ) : null}
    </>
  );
}

function SurchargeAmountInput({ currency }: { currency: Currency }) {
  const { values } = useFormikContext<UpdateSupplierInput>();

  if (values.age_surcharge_type === AgeSurchargeType.Unspecified) {
    return null;
  }

  if (values.age_surcharge_type === AgeSurchargeType.FlatFee) {
    return (
      <CurrencyInput
        currency={currency}
        name={"age_surcharge_amount"}
        label={"Surcharge amount"}
      />
    );
  }

  return (
    <CurrencyInput
      currency={currency}
      name={"age_surcharge_amount"}
      label={"Surcharge amount"}
      suffix="per day"
      viewNode={
        <span>
          {formatCurrency(values.age_surcharge_amount, currency)} /day
        </span>
      }
    />
  );
}

function SupplierAgeSurchargePreview({ currency }: { currency: Currency }) {
  const { values } = useFormikContext<UpdateSupplierInput>();

  if (
    !values.minimum_age ||
    !values.maximum_age ||
    !values.minimum_no_surcharge_age ||
    !values.maximum_no_surcharge_age ||
    !values.age_surcharge_amount
  ) {
    return null;
  }

  return (
    <div className="col-span-full">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="mb-4 text-sm font-medium text-gray-900">
          Age Surcharge Preview
        </p>
        <AgeSurchargeIndicator
          minimumAge={values.minimum_age}
          maximumAge={values.maximum_age}
          minimumNoSurchargeAge={values.minimum_no_surcharge_age}
          maximumNoSurchargeAge={values.maximum_no_surcharge_age}
          ageSurchargeAmount={values.age_surcharge_amount}
          ageSurchargeType={values.age_surcharge_type!}
          currency={currency}
        />
      </div>
    </div>
  );
}
